import { Injectable, Signal, signal } from '@angular/core';

export type SaveFeedbackKind = 'success' | 'error';

export interface SaveFeedback {
  readonly id: number;
  readonly kind: SaveFeedbackKind;
  readonly text: string;
}

/**
 * App-wide "your change was saved" affordance.
 *
 * Generalises the inline `Saved.` span first built on the programme-sync
 * schedule form (docs/TODO.md — Rotordynamics #2: "no 'saved' feedback after
 * an admin update") into ONE toast surface every admin form can call, so a
 * successful save is never silent again. Inject the service and call
 * `saved()` / `error()`; the toasts are rendered once by
 * {@link SaveFeedbackComponent}, mounted in the app layout.
 *
 * Pure logic (no DOM) so it's unit-testable — the component only renders it.
 */
@Injectable({ providedIn: 'root' })
export class SaveFeedbackService {
  /** How long a SUCCESS toast stays before auto-dismissing (ms). Errors
   *  never auto-dismiss — the user should acknowledge a failure. */
  static readonly SuccessDismissMs = 3000;

  private nextId = 1;
  private readonly _toasts = signal<readonly SaveFeedback[]>([]);

  /** Read-only view of the live toasts, for the container component. */
  readonly toasts: Signal<readonly SaveFeedback[]> = this._toasts.asReadonly();

  /** Confirm a successful save. Auto-dismisses after {@link SuccessDismissMs}. */
  saved(text = 'Saved.'): number {
    return this.push('success', text, SaveFeedbackService.SuccessDismissMs);
  }

  /** Report a failed save. Stays until the user dismisses it. */
  error(text = 'Could not save. Please try again.'): number {
    return this.push('error', text, 0);
  }

  /** Remove a toast (manual close, or the auto-dismiss timer). Safe on a
   *  stale id (already-dismissed toast) — a no-op. */
  dismiss(id: number): void {
    this._toasts.update(list => list.filter(t => t.id !== id));
  }

  private push(kind: SaveFeedbackKind, text: string, autoDismissMs: number): number {
    const id = this.nextId++;
    this._toasts.update(list => [...list, { id, kind, text }]);
    if (autoDismissMs > 0) {
      setTimeout(() => this.dismiss(id), autoDismissMs);
    }
    return id;
  }
}
